/* -----------------Globals--------------- */
import { useMemo, useState } from "react";

import Icon from "@expo/vector-icons/MaterialCommunityIcons";
/* -----------------UI--------------- */
import { Image } from "expo-image";
import { List, Text } from "react-native-paper";

import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";

/* -----------------Types--------------- */
import type { UnifiedCitation } from "@memorang/types";
import { Pressable, type StyleProp, type ViewStyle } from "react-native";
import { themeRoundnessMultiplier } from "../../constants";
/* -----------------Child components--------------- */
import Box from "../Box";
import MarkdownRenderer from "../MarkdownRenderer";
import RenderMarkdown from "../MarkdownRenderer";

type ReferencesProps = {
	references: UnifiedCitation[] | string;
	showTextBasedCitationOnly?: boolean;
	handleNavigateToReference?: (url: string) => void;
	onPressReference?: (reference: UnifiedCitation) => void;
	referenceThumbnails?: Record<string, string>;
	withAccordion?: boolean;
	referenceStyle?: StyleProp<ViewStyle>;
};

type ReferenceAccordionProps = {
	children: React.ReactNode;
	title?: string;
};

const ReferenceAccordion = ({
	children,
	title = "Read more",
}: ReferenceAccordionProps) => {
	const theme = useAppTheme();
	const [isAccordionExpanded, setIsAccordianExpanded] = useState(false);

	const handleOnPressAccordion = () => {
		setIsAccordianExpanded(!isAccordionExpanded);
	};

	return (
		<List.Accordion
			right={() => null}
			style={{
				backgroundColor: theme.colors.secondaryContainer,
				// ? This is a hack to remove the margin vertical inside List.Accordion which cannot be removed with custom style
				marginBottom: isAccordionExpanded ? 0 : -16,
			}}
			title={
				<Box flexDirection="row" alignItems="center" gap={4}>
					<Text variant="labelMedium">{title}</Text>
					<List.Icon
						icon={isAccordionExpanded ? "chevron-up" : "chevron-down"}
					/>
				</Box>
			}
			expanded={isAccordionExpanded}
			onPress={handleOnPressAccordion}
		>
			{children}
		</List.Accordion>
	);
};

const Reference = ({
	reference,
	handleNavigateToReference: _handleNavigateToReference,
	onPressReference,
	referenceThumbnail,
	referenceStyle,
	showTextBasedCitationOnly,
}: {
	reference: UnifiedCitation;
	handleNavigateToReference?: (url: string) => void;
	onPressReference?: (reference: UnifiedCitation) => void;
	referenceThumbnail?: string;
	referenceStyle?: StyleProp<ViewStyle>;
	showTextBasedCitationOnly?: boolean;
}) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	const thumbnail = useMemo(() => {
		const imageUri = reference.thumbnailUri || referenceThumbnail;

		if (!imageUri) {
			return (
				<Icon
					name={reference.url ? "web" : "book-open-page-variant-outline"}
					size={16}
					color={theme.colors.onSurfaceVariant}
				/>
			);
		}

		return (
			<Image
				cachePolicy="memory-disk"
				source={{
					uri: imageUri,
				}}
				style={{
					width: isMobile ? 12 : 12,
					height: isMobile ? 16 : 16,
					alignSelf: "center",
				}}
			/>
		);
	}, [reference, referenceThumbnail, isMobile, theme.colors.onSurfaceVariant]);

	if (showTextBasedCitationOnly) {
		return (
			<Box flexDirection="row" gap={8}>
				<Text variant="labelMedium">●</Text>
				<Box flex={1}>
					<RenderMarkdown text={reference.title} fontSize={14} />
				</Box>
			</Box>
		);
	}

	return (
		<Pressable onPress={() => onPressReference?.(reference)}>
			<Box
				style={[
					{
						padding: 8,
						backgroundColor: theme.colors.surface,
						borderRadius: theme.roundness * themeRoundnessMultiplier,
						overflow: "hidden",
						justifyContent: "center",
					},
					referenceStyle,
				]}
			>
				<Box flexDirection="row" alignItems="center" columnGap={8}>
					{thumbnail}
					<Box flex={1}>
						<MarkdownRenderer
							text={reference.title}
							fontSize={14}
							customStyles={{
								text: {
									color: theme.colors.onSurfaceVariant,
								},
							}}
						/>
					</Box>
				</Box>
			</Box>
		</Pressable>
	);
};

export const References = ({
	references,
	handleNavigateToReference,
	onPressReference,
	referenceThumbnails,
	withAccordion = true,
	referenceStyle,
	showTextBasedCitationOnly,
}: ReferencesProps) => {
	const theme = useAppTheme();

	const Wrapper = withAccordion ? ReferenceAccordion : Box;

	const deduplicatedReferences = useMemo(() => {
		const seenTitles = new Set<string>();
		return Array.isArray(references)
			? (references
					.filter((reference) => {
						const title = reference.title.trim().toLowerCase();
						if (seenTitles.has(title)) {
							return false;
						}
						seenTitles.add(title);
						return true;
					})
					.slice(0, 3) ?? [])
			: [];
	}, [references]);

	if (typeof references === "string") {
		return (
			<Wrapper title="Question">
				<Box paddingHorizontal={16}>
					<MarkdownRenderer
						text={references}
						customStyles={{
							text: {
								fontSize: 14,
								lineHeight: 21,
								color: theme.colors.text,
							},
							paragraph: {
								marginBottom: 4,
							},
							image: {
								height: 120,
								width: 120,
								marginTop: 16,
								marginVertical: 16,
							},
						}}
					/>
				</Box>
			</Wrapper>
		);
	}

	return (
		<Wrapper title="Question">
			<Box flexDirection="column" gap={12} paddingRight={16}>
				{deduplicatedReferences.map((item, index) => (
					<Reference
						key={index.toString()}
						reference={item}
						showTextBasedCitationOnly={showTextBasedCitationOnly || !item.url}
						handleNavigateToReference={handleNavigateToReference}
						onPressReference={onPressReference}
						referenceThumbnail={
							item.thumbnailUri ||
							(item.sourceId &&
								referenceThumbnails?.[item.sourceId.toString()]) ||
							(item.id && referenceThumbnails?.[item.id.toString()]) ||
							undefined
						}
						referenceStyle={referenceStyle}
					/>
				))}
			</Box>
		</Wrapper>
	);
};
